import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ErpModule } from '@/api/platform.api';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { moduleAdminPath, moduleSettingsLink } from '@/config/moduleWorkspace';
import { platformBillingApi, subscriptionsApi } from '@/api';
import { AdministrationLayout } from '@/shared/layouts/AdministrationLayout';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Select } from '@/components/ui/Select';
import type {
  BillingModulePlanResponse,
  BillingModuleSubscriptionResponse,
  BillingOverviewResponse,
  BillingUsageMeterResponse,
} from '@/types/platformBilling';
import type {
  OrganizationSubscriptionResponse,
  PaymentHistoryResponse,
  SubscriptionPlanResponse,
} from '@/types/subscription';
import type { BillingCycle } from '@/types/enums';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import { formatCurrency, formatDate } from '@/utils/format';
import { loadRazorpayScript, openRazorpayCheckout } from '@/utils/razorpay';
import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
function UsageMeter({ meter }: { meter: BillingUsageMeterResponse }) {
  const pct = meter.unlimited ? 0 : Math.min(meter.percentUsed, 100);
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-slate-800 dark:text-slate-100">{meter.label}</span>
        <span className="text-slate-500 dark:text-slate-400">
          {meter.unlimited
            ? `${meter.used} / unlimited`
            : `${meter.used} / ${meter.limit ?? '—'}`}
        </span>
      </div>
      {!meter.unlimited && (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={`h-full rounded-full ${meter.exceeded ? 'bg-red-500' : 'bg-primary-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {meter.resetPeriod && meter.resetPeriod !== 'NONE' && (
        <p className="mt-1 text-xs text-slate-500">Resets: {meter.resetPeriod.toLowerCase()}</p>
      )}
    </div>
  );
}

export function BillingSubscriptionsPage({ focusModule }: { focusModule?: ErpModule } = {}) {
  const modulePagination = usePagination(10);
  const paymentsPagination = usePagination(10);
  const moduleLabel = focusModule
    ? (getModuleEntryByErpModule(focusModule)?.label ?? focusModule)
    : null;
  const [overview, setOverview] = useState<BillingOverviewResponse | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlanResponse[]>([]);
  const [payments, setPayments] = useState<PaymentHistoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('MONTHLY');
  const [upgrading, setUpgrading] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string>('POS');
  const [modulePlans, setModulePlans] = useState<BillingModulePlanResponse[]>([]);
  const [modulePlansLoading, setModulePlansLoading] = useState(false);
  const [activatingModule, setActivatingModule] = useState(false);

  const legacySub: OrganizationSubscriptionResponse | null | undefined = overview?.legacySubscription;
  const usageMeters = useMemo(() => {
    const meters = overview?.usageMeters ?? [];
    if (!focusModule) return meters;
    return meters.filter((m) => m.moduleCode === focusModule);
  }, [overview?.usageMeters, focusModule]);

  const moduleSubscriptions = useMemo(() => {
    const rows = overview?.moduleSubscriptions ?? [];
    if (!focusModule) return rows;
    return rows.filter((m) => m.moduleCode === focusModule);
  }, [overview?.moduleSubscriptions, focusModule]);

  useEffect(() => {
    modulePagination.applyClientCount(moduleSubscriptions.length);
  }, [moduleSubscriptions.length, modulePagination]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, plansRes, paymentsRes] = await Promise.all([
        platformBillingApi.overview(),
        subscriptionsApi.listPlans(),
        subscriptionsApi.paymentHistory().catch(() => [] as PaymentHistoryResponse[]),
      ]);
      setOverview(overviewRes);
      setPlans(plansRes);
      setPayments(paymentsRes);
      paymentsPagination.applyClientCount(paymentsRes.length);
      if (focusModule) {
        setSelectedModule(focusModule);
      } else if (overviewRes.moduleSubscriptions.length > 0) {
        setSelectedModule(overviewRes.moduleSubscriptions[0].moduleCode);
      }
    } catch (err) {
      showApiError(err, 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, [focusModule]);

  useEffect(() => {
    if (focusModule) {
      setSelectedModule(focusModule);
    }
  }, [focusModule]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedModule) return;
    setModulePlansLoading(true);
    platformBillingApi
      .modulePlans(selectedModule)
      .then(setModulePlans)
      .catch((err) => showApiError(err, 'Failed to load module plans'))
      .finally(() => setModulePlansLoading(false));
  }, [selectedModule]);

  const moduleOptions = useMemo(
    () =>
      (overview?.moduleSubscriptions ?? []).map((m) => ({
        value: m.moduleCode,
        label: m.moduleCode,
      })),
    [overview?.moduleSubscriptions],
  );

  const selectLegacyPlan = async (plan: SubscriptionPlanResponse) => {
    if (legacySub?.planId === plan.id) {
      notify.success('Already on this plan');
      return;
    }
    setUpgrading(true);
    try {
      const checkout = await subscriptionsApi.checkout({ planId: plan.id, billingCycle });
      if (!checkout.requiresPayment) {
        notify.success(`${plan.name} activated`);
        await load();
        return;
      }
      if (!checkout.razorpayOrder) {
        notify.error('Payment could not be started');
        return;
      }
      await loadRazorpayScript();
      openRazorpayCheckout({
        keyId: checkout.razorpayOrder.keyId,
        orderId: checkout.razorpayOrder.orderId,
        amountPaise: checkout.razorpayOrder.amount,
        currency: checkout.razorpayOrder.currency,
        planName: plan.name,
        onSuccess: async (payment) => {
          try {
            await subscriptionsApi.subscribe({
              planId: plan.id,
              billingCycle,
              razorpayPaymentId: payment.razorpayPaymentId,
              razorpayOrderId: payment.razorpayOrderId,
              razorpaySignature: payment.razorpaySignature,
            });
            notify.success('Subscription upgraded');
            await load();
          } catch (err) {
            showApiError(err, 'Payment verification failed');
          }
        },
      });
    } catch (err) {
      showApiError(err, 'Upgrade failed');
    } finally {
      setUpgrading(false);
    }
  };

  const activateModulePlan = async (plan: BillingModulePlanResponse) => {
    setActivatingModule(true);
    try {
      await platformBillingApi.activateModule(selectedModule, plan.planCode);
      notify.success(`${selectedModule} plan set to ${plan.planName}`);
      await load();
      const refreshed = await platformBillingApi.modulePlans(selectedModule);
      setModulePlans(refreshed);
    } catch (err) {
      showApiError(err, 'Module plan update failed');
    } finally {
      setActivatingModule(false);
    }
  };

  const paymentColumns: Column<PaymentHistoryResponse>[] = [
    { key: 'paymentDate', header: 'Date', render: (r) => formatDate(r.paymentDate) },
    { key: 'planCode', header: 'Plan' },
    { key: 'amount', header: 'Amount', render: (r) => formatCurrency(r.amount) },
    { key: 'paymentStatus', header: 'Status' },
    { key: 'description', header: 'Description' },
  ];

  const moduleColumns: Column<BillingModuleSubscriptionResponse>[] = [
    { key: 'moduleCode', header: 'Module' },
    { key: 'planName', header: 'Plan' },
    { key: 'subscriptionStatus', header: 'Status' },
    {
      key: 'moduleEnabled',
      header: 'App enabled',
      render: (r) => (r.moduleEnabled ? 'Yes' : 'No'),
    },
  ];

  return (
    <AdministrationLayout
      title={moduleLabel ? `${moduleLabel} subscription` : 'Billing & Subscriptions'}
      description={
        moduleLabel
          ? `Plans, usage, and billing for ${moduleLabel}. Feature access follows the active module plan.`
          : 'Organization plan, per-module subscriptions, usage quotas, and payment history.'
      }
    >
      {loading ? (
        <p className="text-sm text-slate-500">Loading billing…</p>
      ) : (
        <div className="space-y-8">
          {!focusModule && (
          <section className="card p-5">
            <h2 className="text-lg font-semibold">Current subscription</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Org-wide platform tier (stores, employees, products). Module features are managed in each module section below.
            </p>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Platform</p>
                <p className="mt-1 text-xl font-semibold">
                  {overview?.platformSubscription?.planName ?? '—'}
                </p>
                <p className="text-sm text-slate-500">
                  Status: {overview?.platformSubscription?.subscriptionStatus ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Organization plan</p>
                <p className="mt-1 text-xl font-semibold">{legacySub?.planName ?? 'Free'}</p>
                <p className="text-sm text-slate-500">
                  {legacySub?.endDate
                    ? `Renews / ends ${formatDate(legacySub.endDate)}`
                    : 'No end date'}
                </p>
              </div>
            </div>
            {overview?.legacyUsage && (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                Limits — stores: {overview.legacyUsage.storesUsed}/{overview.legacyUsage.maxStores ?? '∞'},
                employees: {overview.legacyUsage.employeesUsed}/{overview.legacyUsage.maxEmployees ?? '∞'},
                products: {overview.legacyUsage.productsUsed}/{overview.legacyUsage.maxProducts ?? '∞'}
              </p>
            )}
          </section>
          )}

          <section>
            <h2 className="text-lg font-semibold">Usage & quotas</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {usageMeters.length === 0 ? (
                <p className="text-sm text-slate-500">No meters configured yet.</p>
              ) : (
                usageMeters.map((m) => <UsageMeter key={`${m.moduleCode}-${m.metricCode}`} meter={m} />)
              )}
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">
                {moduleLabel ? `${moduleLabel} subscription` : 'Module subscriptions'}
              </h2>
              {focusModule && (
                <Link to={moduleSettingsLink(focusModule)} className="text-sm font-medium text-primary-600 hover:underline">
                  Module settings
                </Link>
              )}
            </div>
            <DataTable
              columns={moduleColumns}
              data={paginateClientList(moduleSubscriptions, modulePagination.page, modulePagination.size)}
              keyField="moduleCode"
              emptyMessage={moduleLabel ? `No ${moduleLabel} subscription` : 'No module subscriptions'}
              {...dataTablePageProps(modulePagination)}
            />
            <div className="card p-5">
              <h3 className="font-medium">Change module plan</h3>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                {!focusModule && (
                  <div className="min-w-[160px]">
                    <Select
                      label="Module"
                      value={selectedModule}
                      onChange={setSelectedModule}
                      options={moduleOptions}
                    />
                  </div>
                )}
              </div>
              {modulePlansLoading ? (
                <p className="mt-3 text-sm text-slate-500">Loading plans…</p>
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {modulePlans.map((plan) => (
                    <div key={plan.id} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                      <p className="font-semibold">{plan.planName}</p>
                      <p className="text-sm text-slate-500">{plan.planCode}</p>
                      <p className="mt-2 text-sm">
                        {plan.free
                          ? 'Free'
                          : formatCurrency(plan.monthlyPrice ?? 0) + ' / mo'}
                      </p>
                      <Button
                        className="mt-3 w-full"
                        disabled={plan.current || activatingModule}
                        onClick={() => activateModulePlan(plan)}
                      >
                        {plan.current ? 'Current' : 'Select plan'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {!focusModule && (
          <section className="card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Org-wide platform plan</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Legacy organization tier — max stores, employees, and products across the whole account.
                  Use <strong>Change module plan</strong> above for POS, CRM, HR, etc.
                </p>
              </div>
              <Select
                label="Billing cycle"
                value={billingCycle}
                onChange={(v) => setBillingCycle(v as BillingCycle)}
                options={[
                  { value: 'MONTHLY', label: 'Monthly' },
                  { value: 'YEARLY', label: 'Yearly' },
                ]}
              />
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan) => (
                <div key={plan.id} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                  <p className="text-lg font-semibold">{plan.name}</p>
                  <p className="text-sm text-slate-500">{plan.planCode}</p>
                  <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                    <li>Stores: {plan.maxStores ?? '—'}</li>
                    <li>Employees: {plan.maxEmployees ?? '—'}</li>
                    <li>Products: {plan.maxProducts ?? '—'}</li>
                  </ul>
                  <p className="mt-2 font-medium">
                    {plan.free
                      ? 'Free'
                      : billingCycle === 'YEARLY'
                        ? formatCurrency(plan.priceYearly ?? 0) + ' / yr'
                        : formatCurrency(plan.priceMonthly ?? 0) + ' / mo'}
                  </p>
                  <Button
                    className="mt-3 w-full"
                    disabled={upgrading || legacySub?.planId === plan.id}
                    onClick={() => selectLegacyPlan(plan)}
                  >
                    {legacySub?.planId === plan.id ? 'Current plan' : 'Upgrade'}
                  </Button>
                </div>
              ))}
            </div>
          </section>
          )}

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">Payment history</h2>
            <DataTable
              columns={paymentColumns}
              data={paginateClientList(payments, paymentsPagination.page, paymentsPagination.size)}
              keyField="id"
              emptyMessage="No payments yet"
              {...dataTablePageProps(paymentsPagination)}
            />
          </section>

          <p className="text-sm text-slate-500">
            <Link
              to={focusModule ? moduleAdminPath(focusModule, 'administration') : '/pos/administration'}
              className="text-primary-600 hover:underline"
            >
              ← Back to Administration
            </Link>
          </p>
        </div>
      )}
    </AdministrationLayout>
  );
}
